import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch, useLocation } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import Home from "./pages/Home";
import AboutDuraTarpPage from "./pages/AboutDuraTarpPage";
import { ApplicationDetail, ApplicationDirectory, CustomSolutions, ProductDetail, ProductDirectory } from "./pages/ProductPages";
import { CaseStudiesPage, CertificationsPage, ContactPage, ManufacturingPage, QualityPage, ResourcesPage, TechnicalCenterPage } from "./pages/InfoPages";
import { TechnicalArticleDetail } from "./pages/TechnicalPages";
import { ExtendedProductDetail, FullProductDirectory } from "./pages/FullProductPages";
import ProductFinderPage from "./pages/ProductFinderPage";
import { SpecificProductDetail, SpecificProductDirectory } from "./pages/SpecificProductPages";
import { MaterialsHub, PerformanceDetail, PerformanceDirectory, SpecificationsPage } from "./pages/SolutionPages";
import ContentLibraryPage from "./pages/ContentLibraryPage";
import QuickQuoteDock from "./components/QuickQuoteDock";
import JsonLd from "./components/JsonLd";
import { PUBLIC_SITE_ORIGIN } from "./lib/seo";

function Router() {
  // make sure to consider if you need authentication for certain routes
  return (
    <Switch>
      <Route path={"/"} component={Home} />
      <Route path={"/content-library"} component={ContentLibraryPage} />
      <Route path={"/products"} component={SpecificProductDirectory} />
      <Route path={"/products/:skuSlug"} component={SpecificProductDetail} />
      <Route path={"/materials"} component={MaterialsHub} />
      <Route path={"/materials/:matrixSlug"} component={ExtendedProductDetail} />
      <Route path={"/material-routes"} component={ProductFinderPage} />
      <Route path={"/applications"} component={ApplicationDirectory} />
      <Route path={"/applications/:applicationSlug"} component={ApplicationDetail} />
      <Route path={"/performance"} component={PerformanceDirectory} />
      <Route path={"/performance/:performanceSlug"} component={PerformanceDetail} />
      <Route path={"/specifications"} component={SpecificationsPage} />
      <Route path={"/custom-pvc-tarpaulin"} component={CustomSolutions} />
      <Route path={"/manufacturing"} component={ManufacturingPage} />
      <Route path={"/factory"} component={ManufacturingPage} />
      <Route path={"/quality-control"} component={QualityPage} />
      <Route path={"/quality"} component={QualityPage} />
      <Route path={"/manufacturer"} component={AboutDuraTarpPage} />
      <Route path={"/technical-center"} component={TechnicalCenterPage} />
      <Route path={"/technical-center/:articleSlug"} component={TechnicalArticleDetail} />
      <Route path={"/resources"} component={ResourcesPage} />
      <Route path={"/about-us"} component={AboutDuraTarpPage} />
      <Route path={"/certifications"} component={CertificationsPage} />
      <Route path={"/case-studies"} component={CaseStudiesPage} />
      <Route path={"/contact"} component={ContactPage} />
      <Route path={"/:productSlug"} component={ProductDetail} />
      <Route path={"/404"} component={NotFound} />
      {/* Final fallback route */}
      <Route component={NotFound} />
    </Switch>
  );
}

function ScrollToTop() {
  const [location] = useLocation();

  useEffect(() => {
    if (!window.location.hash) {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, [location]);

  return null;
}

// NOTE: About Theme
// - First choose a default theme according to your design style (dark or light bg), than change color palette in index.css
//   to keep consistent foreground/background color across components
// - If you want to make theme switchable, pass `switchable` ThemeProvider and use `useTheme` hook

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider
        defaultTheme="light"
        // switchable
      >
        <TooltipProvider>
          <Toaster />
          <JsonLd data={{ "@context": "https://schema.org", "@type": "Organization", name: "DuraTarp", description: "PVC tarpaulin manufacturer and custom industrial tarpaulin solutions", url: PUBLIC_SITE_ORIGIN, email: "sales@duratarp.cn" }} />
          <ScrollToTop />
          <Router />
          <QuickQuoteDock />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
